import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { ApInsertionValidator } from './validators/insertion.validator';
import { ApMatchFirstnamesValidator } from './validators/match-first-names.validator';
import { ApMatchNamesService } from './validators/match-names.service';
import { FieldLengths, Patterns } from './personal-details.expressions';
import { PersonalDetails, PersonalDetailsForm } from './personal-details.model';

/** The ApPersonalDetailsService is used to create a formGroup. To be used with {@link ApPersonalDetailsComponent} */
@Injectable()
export class ApPersonalDetailsService {
    private readonly matchNamesService = inject(ApMatchNamesService);

    public createFormGroup(personalDetails: PersonalDetails): FormGroup<PersonalDetailsForm> {
        const formGroup = new FormGroup<PersonalDetailsForm>(
            {
                gender: new FormControl<string>(personalDetails.gender, {
                    validators: Validators.required,
                    updateOn: 'change'
                }),
                initials: new FormControl<string>(personalDetails.initials, {
                    validators: [Validators.maxLength(FieldLengths.INITIALS), Validators.required]
                }),
                insertion: new FormControl<string>(personalDetails.insertion, {
                    validators: [
                        Validators.maxLength(FieldLengths.INSERTION),
                        Validators.pattern(Patterns.INSERTION),
                        ApInsertionValidator.createValidator()
                    ]
                }),
                lastName: new FormControl<string>(personalDetails.lastName, {
                    validators: [Validators.maxLength(FieldLengths.LASTNAME), Validators.required, Validators.pattern(Patterns.LASTNAME)]
                }),
                firstNames: new FormControl<string>(personalDetails.firstNames, {
                    validators: [
                        Validators.maxLength(FieldLengths.FIRSTNAMES),
                        Validators.required,
                        Validators.pattern(Patterns.FIRST_NAMES),
                        Validators.minLength(2)
                    ]
                }),
                givenName: new FormControl<string>(personalDetails.givenName, {
                    validators: [Validators.maxLength(FieldLengths.GIVENNAME), Validators.pattern(Patterns.GIVEN_NAME)]
                })
            },
            { updateOn: 'blur' }
        );

        formGroup.setValidators([ApMatchFirstnamesValidator.createValidator(this.matchNamesService)('initials', 'firstNames')]);

        return formGroup;
    }

    public addToFormGroup(formGroup: FormGroup, personalDetails: PersonalDetails): void {
        const newFormGroup = this.createFormGroup(personalDetails);
        Object.keys(newFormGroup.controls).forEach((key) => formGroup.addControl(key, newFormGroup.controls[key]));
    }
}
